/**
 * Typing Effects Settings Component
 *
 * Provides UI for configuring typing effects including audio,
 * visual effects, and performance settings.
 */

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Play } from 'lucide-react';
import { TypingEffectsConfig } from './typing-effects-manager';
import { DEFAULT_TYPING_SOUNDS } from './typing-audio-manager';

interface TypingEffectsSettingsProps {
  config: TypingEffectsConfig;
  onConfigChange: (config: Partial<TypingEffectsConfig>) => void;
}

export function TypingEffectsSettings({
  config,
  onConfigChange
}: TypingEffectsSettingsProps) {

  const playPreview = async (soundName: string) => {
    if (soundName === 'none') return;

    const sound = DEFAULT_TYPING_SOUNDS.find(s => s.name === soundName);
    if (!sound?.url) return;

    try {
      // Create a temporary audio element to play the preview
      const audio = new Audio(sound.url);
      audio.volume = config.audioVolume || 0.3;

      // Special handling for heartbeat sound
      if (soundName === 'heartbeat') {
        console.log('🎵 Playing heartbeat preview:', sound.url);
        audio.volume = (config.audioVolume || 0.3) * 0.8; // Slightly quieter for preview

        // Test if file is accessible first
        try {
          const testResponse = await fetch(sound.url);
          if (!testResponse.ok) {
            throw new Error(`File not accessible: ${testResponse.status}`);
          }
          console.log('✅ Heartbeat file is accessible via HTTP');
        } catch (fetchError) {
          console.error('❌ Heartbeat file fetch failed:', fetchError);
          throw new Error(`Cannot access heartbeat file: ${fetchError}`);
        }
      }

      // Handle browser autoplay restrictions
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
      console.log(`✅ Successfully played ${soundName} preview`);
    } catch (error) {
      console.warn(`Failed to play sound preview for ${soundName}:`, error);

      // Show user-friendly error for heartbeat
      if (soundName === 'heartbeat') {
        console.error('❌ Heartbeat sound preview failed. Check if heartbeat.mp3 exists and is valid.');
      }
    }
  };

  return (
    <div className="space-y-6">
          {/* Audio Settings */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="audio-enabled">Typing Sounds</Label>
                <p className="text-sm text-muted-foreground">Play typewriter sounds while typing</p>
              </div>
              <Switch
                id="audio-enabled"
                checked={config.audioEnabled}
                onCheckedChange={(checked) => onConfigChange({ audioEnabled: checked })}
              />
            </div>

            {config.audioEnabled && (
              <div className="space-y-3 ml-4">
                <Label>Sound Type</Label>
                <div className="flex gap-2">
                  <Select
                    value={config.currentSound}
                    onValueChange={(value) => onConfigChange({ currentSound: value })}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a sound" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {DEFAULT_TYPING_SOUNDS.filter(sound => sound.url && sound.name !== 'heartbeat').map(sound => (
                        <SelectItem key={sound.name} value={sound.name}>
                          {sound.name.charAt(0).toUpperCase() + sound.name.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => playPreview(config.currentSound)}
                    disabled={config.currentSound === 'none'}
                    className="px-3"
                    title="Preview sound"
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Visual Effects Settings */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="visual-enabled">Visual Effects</Label>
                <p className="text-sm text-muted-foreground">Show confetti and animations while typing</p>
              </div>
              <Switch
                id="visual-enabled"
                checked={config.visualEffectsEnabled}
                onCheckedChange={(checked) => onConfigChange({
                  visualEffectsEnabled: checked,
                  confettiEnabled: checked // Auto-enable confetti with visual effects
                })}
              />
            </div>

            {config.visualEffectsEnabled && (
              <div className="space-y-2 ml-4">
                <Label>Effect Type</Label>
                <Select
                  value={config.confettiType}
                  onValueChange={(value: any) => onConfigChange({ confettiType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select effect type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Confetti</SelectItem>
                    <SelectItem value="stars">Stars</SelectItem>
                    <SelectItem value="emojis">Emojis</SelectItem>
                    <SelectItem value="maple-leaves">Maple Leaves</SelectItem>
                    <SelectItem value="snow-prism">Snow Prism</SelectItem>
                    <SelectItem value="hearts">Hearts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
    </div>
  );
}

// TypingEffectsQuickSettings removed - settings only managed in main settings page
